// package imports
import { KitButton } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TurnLeftIcon from '@mui/icons-material/TurnLeft';
import { Box, Snackbar, Tabs } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useIsFetching } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import React, { useEffect, useState } from 'react';

// local imports
import { logClientException } from 'appinsights/clientAppInsights';
import PleaseWait from 'components/common/PleaseWait';
import RedirectDialog from 'components/common/subcomponents/RedirectDialog';
import { BadRequestError } from 'services/apiWrapper';
import ObservationHeader from './components/ObservationHeader';
import ObservationPageContainer from './components/ObservationPageContainer';
import {
  canAccessObservations,
  observationRedirectMessage,
  observationRedirectPage,
} from './utils/ObservationUtils';
import { useObservationEventInstanceList } from './hooks/ObservationEventHooks';
import EventHeaderDetail from './components/eventSummary/EventHeaderDetail';
import EventSummaryStats from './utils/EventSummaryStats';
import EventSummaryMetricCards from './components/eventSummary/EventSummaryMetricCards';
import PreventionBenchmarkGroup from './components/sessionSummary/PreventionBenchmarkGroup';
import PraiseBenchmarkGroup from './components/sessionSummary/PraiseBenchmarkGroup';
import CoorectionBenchmarkGroup from './components/sessionSummary/CorrectionBenchmarkGroup';
import TabPanel from './components/TabPanel';
import { TabItem } from './components/Tabs';
import EventSummaryNotes from './components/eventSummary/EventSummaryNotes';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { isCurrentDateInRange } from '../../../services/dateUtils';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

const ObservationEventSummary = ({ module: { fields } }) => {
  const { data: session, status } = useSession();
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [event, setEvent] = useState(null);
  const [instanceList, setInstanceList] = useState([]);
  const queryParams =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search);
  const eventInstanceListQuery = useObservationEventInstanceList(eventId);
  const isFetchingCount = useIsFetching();
  const eventStats = new EventSummaryStats(instanceList);

  useEffect(() => {
    if (
      eventInstanceListQuery.isSuccess &&
      eventInstanceListQuery.isFetching === false
    ) {
      setEvent(eventInstanceListQuery.data.event);
      setInstanceList(eventInstanceListQuery.data.instances);
    }
    if (eventInstanceListQuery.isError) {
      logClientException(eventInstanceListQuery.error);
      if (
        eventInstanceListQuery.error?.message
          ?.toLocaleLowerCase()
          .includes('401')
      ) {
        // not signed in, should get redirect popup
      } else if (eventInstanceListQuery.error instanceof BadRequestError) {
        setErrorMessage(eventInstanceListQuery.error.formatMessage());
      } else {
        setErrorMessage(defaultErrorMessage);
      }
    }
  }, [eventInstanceListQuery]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      const id = queryParams?.get('id');
      if (id && id?.length > 0) {
        setEventId(id);
      }
    }
  }, [queryParams?.get('id')]);

  // if the query string is targeting notes tab
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tab') === 'notes') {
        setTabIndex(1);
        params.delete('tab');
        const newSearch = params.toString();
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  if (
    initialized &&
    (!eventId || !event) &&
    eventInstanceListQuery.isSuccess &&
    eventInstanceListQuery.isFetching === false
  ) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.obsBlue.light,
          paddingBottom: '3rem',
        }}
      >
        <ObservationHeader
          title={fields.title}
          headerAccentImageUrl={fields.headerAccentImage?.url}
          size="small"
        />
        <ObservationPageContainer>
          <div>
            {fields?.noIdMessage ||
              'You appear to have reached this page in error.'}
          </div>
          <KitButton
            size="sm"
            style={{
              width: '100px',
            }}
            href={`${fields?.returnPage?.href}?r=1`}
          >
            <TurnLeftIcon />
            Back
          </KitButton>
        </ObservationPageContainer>
      </Box>
    );
  }

  const newObservationLink = fields?.newObservationLink;
  const showNewObservationButton = isCurrentDateInRange(event?.start_date, event?.end_date);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.obsBlue.light,
        paddingBottom: '3rem',
      }}
    >
      <ObservationHeader
        title={fields.title}
        headerAccentImageUrl={fields.headerAccentImage?.url}
        size="large"
      >
        <RedirectDialog
          open={
            (status !== 'loading' && status !== 'authenticated') ||
            !canAccessObservations(session, status)
          }
          message={observationRedirectMessage(session, status)}
          redirectTo={observationRedirectPage(session, status)}
          buttonText={session?.user ? 'OK' : 'Sign In'}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <EventHeaderDetail event={event} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <KitButton
              size="sm"
              style={{
                width: '180px',
              }}
              disabled={isFetchingCount > 0}
              href={`${fields?.returnPage?.href}?r=1`}
            >
              <TurnLeftIcon />
              {fields?.returnPage?.text}
            </KitButton>
            {newObservationLink && newObservationLink.href && (
              <KitButton
                size='sm'
                style={{
                  backgroundColor: theme.palette.primary.main,
                  width: '180px',
                }}
                disabled={isFetchingCount > 0 || showNewObservationButton === false}
                href={`${newObservationLink.href}?id=${eventId}`}
              >
                <AddCircleIcon />
                {newObservationLink.text}
              </KitButton>
            )}
            <KitButton
              size="sm"
              style={{
                width: '180px',
              }}
              disabled={
                isFetchingCount > 0 || eventInstanceListQuery.isFetching
              }
              target="_blank"
              href={`/api/db/observation/event-summary-download?id=${eventId}`}
            >
              <SaveAltIcon />
              Download
            </KitButton>
          </Box>
        </Box>
      </ObservationHeader>
      <ObservationPageContainer>
        <Box
          sx={{
            fontSize: '24px',
            lineHeight: '30px',
            fontWeight: 600,
            color: theme.palette.obsBlue.main,
            mt: '.5rem',
            mb: '.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: 10 }}>
              {eventStats.NumTeachers} Teachers Observed{' '}
            </div>
            <KitButton
              size="sm"
              style={{
                width: '150px',
              }}
              href={`session-list?id=${eventId}&p=summary`}
            >
              View Event Sessions
            </KitButton>
          </div>
          {/* <div>{calcPercent(eventStats.NumTeachersMeetingAllBenchmarks, eventStats.NumTeachers)}% of Observed Teachers Meeting Benchmarks</div> */}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '.5rem',
            justifyContent: 'center',
          }}
        >
          <EventSummaryMetricCards eventSummaryMetrics={eventStats} />

          <Box sx={{ width: '100%', marginTop: '1.25rem' }}>
            <Tabs
              value={tabIndex}
              onChange={(e, nv) => setTabIndex(nv)}
              aria-label="Summary Tabs"
              centered
              variant="fullWidth"
              sx={theme => ({
                borderBottom: `solid 2px ${theme.palette.primary.main}`
              })}
            >
              <TabItem
                label="Benchmarks"
                id="benchmarks-tab"
                aria-controls="benchmarks-tabpanel"
              />
              <TabItem
                label="Event Notes"
                id="event-notes-tab"
                aria-controls="event-notes-tabpanel"
              />
            </Tabs>

            <TabPanel
              value={tabIndex}
              index={0}
              id="benchmarks-tabpanel"
              name="benchmarks-tabpanel"
            >
              <PreventionBenchmarkGroup observationMetrics={eventStats} />
              <PraiseBenchmarkGroup observationMetrics={eventStats} />
              <CoorectionBenchmarkGroup observationMetrics={eventStats} />
            </TabPanel>
            <TabPanel
              value={tabIndex}
              index={1}
              id="event-notes-tabpanel"
              name="event-notes-tabpanel"
            >
              <EventSummaryNotes event={event} />
            </TabPanel>
          </Box>
        </Box>
        <Snackbar
          open={errorMessage}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            sx={{ width: '100%' }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        <PleaseWait isLoading={isFetchingCount > 0} />

        {/* <pre>{JSON.stringify(fields, null, 2)}</pre> */}
      </ObservationPageContainer>
    </Box>
  );
};

export default ObservationEventSummary;
